import React, { useEffect, useMemo, useState } from 'react';
import Header from './Header';
import Footer from './Footer';
import PageHero from './PageHero';
import API_CONFIG from '../config/api';
import { getAllCMSImages } from '../services/s3Service';
import { getFirstCachedImage } from '../utils/cmsCache';
import { SkeletonRecordsTable } from './Skeleton';
import '../styles/records/RecordsPage.css';

const EDAD = {
  subjunior: 'Sub-Junior',
  junior: 'Junior',
  open: 'Open',
  master_i: 'Master I',
  master_ii: 'Master II',
  master_iii: 'Master III',
  master_iv: 'Master IV',
  master_v: 'Master V',
};

const etiqueta = (valor) => {
  if (!valor) return '—';
  if (EDAD[valor]) return EDAD[valor];
  if (valor === 'tested') return 'Tested';
  if (valor === 'untested') return 'Untested';
  if (valor === 'classic' || valor === 'classic_raw') return 'Classic Raw';
  if (valor === 'raw') return 'Raw';
  if (valor === 'single_ply') return 'Single ply';
  return valor.charAt(0).toUpperCase() + valor.slice(1).replace(/_/g, ' ');
};

const kg = (valor) => (valor == null || valor === '' ? '—' : valor);
const dots = (valor) => (valor == null || valor === '' ? '—' : Number(valor).toFixed(2));

const tituloGrupo = (grupo, tipo) => {
  const partes = [etiqueta(grupo.modalidad), grupo.genero];
  if (tipo === 'categoria') partes.push(etiqueta(grupo.categoria_edad));
  partes.push(etiqueta(grupo.tipo_competencia));
  if (tipo === 'categoria' && grupo.categoria_peso) partes.push(grupo.categoria_peso);
  return partes.filter(Boolean).join(' · ');
};

const RankingsPage = () => {
  const [heroImage, setHeroImage] = useState(() => getFirstCachedImage(['page_hero_rankings', 'page_hero_records']));
  const [tipo, setTipo] = useState('overall');
  const [anio, setAnio] = useState('');
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [errorCarga, setErrorCarga] = useState(null);
  const [filtros, setFiltros] = useState({
    modalidad: '',
    genero: '',
    categoria_edad: '',
    tipo_competencia: '',
    categoria_peso: '',
    buscar: '',
  });

  useEffect(() => {
    getAllCMSImages().then((imgs) => setHeroImage(imgs?.page_hero_rankings || imgs?.page_hero_records || null))
      .catch(() => setHeroImage(null));
  }, []);

  useEffect(() => {
    const params = new URLSearchParams({ tipo });
    if (anio) params.set('anio', anio);
    const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.RANKINGS.PUBLICO}?${params}`;
    let cancelado = false;
    setCargando(true);
    setErrorCarga(null);
    fetch(url, { method: 'GET', headers: { Accept: 'application/json' }, cache: 'no-store' })
      .then((res) => {
        if (!res.ok) throw new Error(`Error ${res.status} al cargar el ranking`);
        return res.json();
      })
      .then((data) => {
        if (cancelado) return;
        if (data && data.success === false) throw new Error(data.error || 'No se pudo cargar el ranking');
        setDatos(data);
        if (!anio && data?.anio) setAnio(String(data.anio));
      })
      .catch((err) => {
        if (cancelado) return;
        setErrorCarga(err?.message || 'No se pudo cargar el ranking.');
        setDatos({ anio: anio || null, tipo, anios: [], grupos: [] });
      })
      .finally(() => {
        if (!cancelado) setCargando(false);
      });
    return () => {
      cancelado = true;
    };
  }, [tipo, anio]);

  const grupos = useMemo(() => datos?.grupos || [], [datos]);
  const opciones = useMemo(() => {
    const unicos = (campo) => [...new Set(grupos.map((g) => g[campo]).filter(Boolean))];
    return {
      modalidad: unicos('modalidad'),
      genero: unicos('genero'),
      categoria_edad: unicos('categoria_edad'),
      tipo_competencia: unicos('tipo_competencia'),
      categoria_peso: unicos('categoria_peso'),
    };
  }, [grupos]);

  const gruposVisibles = useMemo(() => {
    const buscar = filtros.buscar.trim().toLowerCase();
    return grupos.filter((grupo) => {
      if (filtros.modalidad && grupo.modalidad !== filtros.modalidad) return false;
      if (filtros.genero && grupo.genero !== filtros.genero) return false;
      if (tipo === 'categoria' && filtros.categoria_edad && grupo.categoria_edad !== filtros.categoria_edad) return false;
      if (filtros.tipo_competencia && grupo.tipo_competencia !== filtros.tipo_competencia) return false;
      if (tipo === 'categoria' && filtros.categoria_peso && grupo.categoria_peso !== filtros.categoria_peso) return false;
      if (!buscar) return true;
      return (grupo.atletas || []).some((a) => (a.nombre || '').toLowerCase().includes(buscar));
    }).map((grupo) => {
      const buscar = filtros.buscar.trim().toLowerCase();
      if (!buscar) return grupo;
      return {
        ...grupo,
        atletas: (grupo.atletas || []).filter((a) => (a.nombre || '').toLowerCase().includes(buscar)),
      };
    });
  }, [grupos, filtros, tipo]);

  const anios = datos?.anios?.length ? datos.anios : (anio ? [Number(anio)] : []);
  const hayResultados = gruposVisibles.some((grupo) => (grupo.atletas || []).length > 0);

  const cambiarFiltro = (campo, valor) => setFiltros((prev) => ({ ...prev, [campo]: valor }));

  const resetearFiltros = () => {
    setFiltros({
      modalidad: '',
      genero: '',
      categoria_edad: '',
      tipo_competencia: '',
      categoria_peso: '',
      buscar: '',
    });
  };

  const columnas = [
    { key: 'puesto', label: 'Ranking', clase: 'ranking-col--puesto' },
    { key: 'nombre', label: 'Atleta', clase: 'ranking-col--atleta' },
    { key: 'squat', label: 'SQ', clase: 'ranking-col--lift' },
    { key: 'bench', label: 'BP', clase: 'ranking-col--lift' },
    { key: 'dead', label: 'DL', clase: 'ranking-col--lift' },
    { key: 'total', label: 'Total' },
    { key: 'dots', label: 'DOTS' },
    { key: 'peso', label: 'BW' },
    ...(tipo === 'overall' ? [{ key: 'categoria', label: 'CAT' }] : []),
    { key: 'meet', label: 'Competencia', clase: 'ranking-col--meet' },
  ];

  return (
    <div className="records-page" style={{ minHeight: '100vh', background: '#fafaf9', color: '#1c1917' }}>
      <Header />
      <PageHero
        imageUrl={heroImage}
        eyebrow="Resultados"
        title={anio ? `Ranking ${anio}` : 'Rankings'}
        subtitle="Ranking anual de powerlifting. Overall por DOTS o por categoría según el total."
      />
      <div className="records-page-wrap">
        <section className="records-mast" aria-label="Filtros">
          <div className="records-mast-header">
            <span className="records-mast-title">Filtros</span>
          </div>
          <div className="records-filters-inner records-filters-selects">
            <div className="records-filter-group">
              <label htmlFor="ranking-anio" className="records-filter-label">Año</label>
              <select
                id="ranking-anio"
                className={`records-select ${anio ? 'records-select--active' : ''}`}
                value={anio}
                onChange={(e) => setAnio(e.target.value)}
                aria-label="Año del ranking"
              >
                {anios.length === 0 && <option value="">Sin años</option>}
                {anios.map((valor) => (
                  <option key={valor} value={valor}>{valor}</option>
                ))}
              </select>
            </div>
            <div className="records-filter-group">
              <label htmlFor="ranking-tipo" className="records-filter-label">Ranking</label>
              <select
                id="ranking-tipo"
                className="records-select records-select--active"
                value={tipo}
                onChange={(e) => setTipo(e.target.value)}
                aria-label="Tipo de ranking"
              >
                <option value="overall">Overall (DOTS)</option>
                <option value="categoria">Por categoría (total)</option>
              </select>
            </div>
            <div className="records-filter-group">
              <label htmlFor="ranking-modalidad" className="records-filter-label">Modalidad</label>
              <select
                id="ranking-modalidad"
                className={`records-select ${filtros.modalidad ? 'records-select--active' : ''}`}
                value={filtros.modalidad}
                onChange={(e) => cambiarFiltro('modalidad', e.target.value)}
              >
                <option value="">Todas</option>
                {opciones.modalidad.map((valor) => (
                  <option key={valor} value={valor}>{etiqueta(valor)}</option>
                ))}
              </select>
            </div>
            <div className="records-filter-group">
              <label htmlFor="ranking-genero" className="records-filter-label">División (sexo)</label>
              <select
                id="ranking-genero"
                className={`records-select ${filtros.genero ? 'records-select--active' : ''}`}
                value={filtros.genero}
                onChange={(e) => cambiarFiltro('genero', e.target.value)}
              >
                <option value="">Todos</option>
                {opciones.genero.map((valor) => (
                  <option key={valor} value={valor}>{valor}</option>
                ))}
              </select>
            </div>
            {tipo === 'categoria' && (
              <div className="records-filter-group">
                <label htmlFor="ranking-edad" className="records-filter-label">Categoría edad</label>
                <select
                  id="ranking-edad"
                  className={`records-select ${filtros.categoria_edad ? 'records-select--active' : ''}`}
                  value={filtros.categoria_edad}
                  onChange={(e) => cambiarFiltro('categoria_edad', e.target.value)}
                >
                  <option value="">Todas</option>
                  {opciones.categoria_edad.map((valor) => (
                    <option key={valor} value={valor}>{etiqueta(valor)}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="records-filter-group">
              <label htmlFor="ranking-tested" className="records-filter-label">Tipo competencia</label>
              <select
                id="ranking-tested"
                className={`records-select ${filtros.tipo_competencia ? 'records-select--active' : ''}`}
                value={filtros.tipo_competencia}
                onChange={(e) => cambiarFiltro('tipo_competencia', e.target.value)}
              >
                <option value="">Todos</option>
                {opciones.tipo_competencia.map((valor) => (
                  <option key={valor} value={valor}>{etiqueta(valor)}</option>
                ))}
              </select>
            </div>
            {tipo === 'categoria' && (
              <div className="records-filter-group">
                <label htmlFor="ranking-peso" className="records-filter-label">Categoría peso</label>
                <select
                  id="ranking-peso"
                  className={`records-select ${filtros.categoria_peso ? 'records-select--active' : ''}`}
                  value={filtros.categoria_peso}
                  onChange={(e) => cambiarFiltro('categoria_peso', e.target.value)}
                >
                  <option value="">Todas</option>
                  {opciones.categoria_peso.map((valor) => (
                    <option key={valor} value={valor}>{valor}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="records-search-row">
              <div className="records-search-wrap">
                <label htmlFor="ranking-buscar" className="records-filter-label">Buscar</label>
                <input
                  id="ranking-buscar"
                  type="search"
                  className="records-search-input"
                  placeholder="Nombre del atleta"
                  value={filtros.buscar}
                  onChange={(e) => cambiarFiltro('buscar', e.target.value)}
                  aria-label="Buscar atleta"
                />
              </div>
              <button type="button" onClick={resetearFiltros} className="records-reset">Restablecer</button>
            </div>
          </div>
        </section>

        {cargando && (
          <div style={{ marginTop: '1rem' }}>
            <SkeletonRecordsTable rows={8} />
          </div>
        )}

        {!cargando && (
          <>
            <p className="records-stats">
              {errorCarga
                ? <span style={{ color: '#b91c1c' }}>{errorCarga}</span>
                : tipo === 'overall'
                  ? 'Overall por DOTS: raw o classic, género y tested/untested. Sin edad ni categoría de peso.'
                  : 'Por categoría de peso, edad, modalidad y tested/untested. El puesto sale del total.'}
            </p>
            {hayResultados ? gruposVisibles.map((grupo) => (
              <section key={tituloGrupo(grupo, tipo)} style={{ marginBottom: '2rem' }}>
                <h2 className="rankings-group-title">
                  {tituloGrupo(grupo, tipo)}
                </h2>
                <div className="records-table-wrap">
                  <table className="records-table rankings-table">
                    <thead className="records-thead">
                      <tr>
                        {columnas.map((col) => (
                          <th key={col.key} className={col.clase}>{col.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {grupo.atletas.map((atleta) => (
                        <tr
                          key={`${atleta.clave_atleta}-${atleta.puesto}`}
                          className={atleta.puesto <= 3 ? `ranking-row--medal ach-medal-${atleta.puesto}` : undefined}
                        >
                          <td className="records-cell ranking-col--puesto">{atleta.puesto}</td>
                          <td className="records-cell records-cell--athlete ranking-col--atleta">{atleta.nombre}</td>
                          <td className="records-cell ranking-col--lift">{kg(atleta.squat)}</td>
                          <td className="records-cell ranking-col--lift">{kg(atleta.bench_press)}</td>
                          <td className="records-cell ranking-col--lift">{kg(atleta.deadlift)}</td>
                          <td className="records-cell records-cell--kg">{kg(atleta.total)}</td>
                          <td className="records-cell ranking-dots">{dots(atleta.dots)}</td>
                          <td className="records-cell">{kg(atleta.peso_corporal)}</td>
                          {tipo === 'overall' && <td className="records-cell">{atleta.categoria_peso || '—'}</td>}
                          <td className="records-cell ranking-col--meet">
                            {atleta.meet_nombre || '—'}
                            {atleta.meet_fecha ? ` · ${atleta.meet_fecha}` : ''}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )) : (
              <div className="records-empty-wrap">
                <h3 className="records-empty-title">
                  {(datos?.grupos || []).length === 0 ? 'Sin datos' : 'Ningún resultado coincide'}
                </h3>
                <p className="records-empty-text">
                  {(datos?.grupos || []).length === 0
                    ? 'Todavía no hay un ranking cargado para este año.'
                    : 'Ajusta los filtros para ver resultados.'}
                </p>
              </div>
            )}
          </>
        )}

        <section className="records-info-section" aria-label="Información">
          <div className="records-info-grid">
            <div className="records-info-block">
              <h4>Overall</h4>
              <p>Ocho listas: género, raw o classic, y tested o untested. El orden es por DOTS.</p>
            </div>
            <div className="records-info-block">
              <h4>Por categoría</h4>
              <p>Se separa además por edad y categoría de peso. El orden es por el total.</p>
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </div>
  );
};

export default RankingsPage;
